import { useAtomValue } from 'jotai';

import { convertUnit, isSpeedDisplayUnit, calculateStatus } from '@core';
import { useAnalysisById } from '@hooks';
import { activityUnitAtom, comparisonAnalysisAtom } from '@store';
import { Box, u } from '@ui';

import { CorridorMargins } from './TableModuleGrid';
import * as css from './TableModuleGrid.css';

type CellProps = {
    isLast: boolean;
    measurementKey: string;
};

export const ComparisonCell = ({ isLast, measurementKey }: CellProps) => {
    const comparisonAnalysis = useAtomValue(comparisonAnalysisAtom);
    const analysis = comparisonAnalysis?.fullAnalysis ?? comparisonAnalysis?.quickAnalysis;
    const measurementValue = analysis?.data.analysis?.parameter_values?.[measurementKey];
    const userPreferredUnit = useAtomValue(activityUnitAtom);
    const converted = measurementValue?.unit
        ? convertUnit({ value: measurementValue?.value, unit: measurementValue?.unit, userPreferredUnit })
        : { ...measurementValue, symbol: '' };
    return (
        <Box
            display={'flex'}
            justifyContent={'flex-end'}
            alignItems={'flex-end'}
            paddingInlineEnd={u(2)}
            paddingBlock={u(1)}
            className={css.comparisonCell({ isLast })}
        >
            {converted.value}
            {(converted.unit && isSpeedDisplayUnit(converted.unit)) || converted.symbol === 'cm' ? (
                <Box style={{ fontSize: '0.667em' }}>{converted.symbol}</Box>
            ) : (
                converted.symbol
            )}
        </Box>
    );
};

type SwingCellProps = CellProps & {
    goalCorridor?: CorridorMargins;
    corridor?: CorridorMargins;
    swingUUID: string;
};

export const SwingCell = ({ isLast, swingUUID, goalCorridor, corridor, measurementKey }: SwingCellProps) => {
    const analysis = useAnalysisById(swingUUID);
    const measurementValue = analysis?.data.analysis?.parameter_values?.[measurementKey];
    const userPreferredUnit = useAtomValue(activityUnitAtom);
    // Defensive guard:
    // Some historical / corrupt swing sessions contain values without a valid unit.
    // `convertUnit` throws if `unit` is invalid or missing, so we bypass conversion
    // and render the raw value with no symbol in those cases.
    const converted = measurementValue?.unit
        ? convertUnit({ value: measurementValue?.value, unit: measurementValue?.unit, userPreferredUnit })
        : { ...measurementValue, symbol: '' };
    const goalStatus = goalCorridor
        ? calculateStatus({
              value: converted.value,
              corridorParameterMargins: { innerMax: goalCorridor.innerMax, innerMin: goalCorridor.innerMin },
          })
        : null;
    const corridorStatus = corridor
        ? calculateStatus({
              value: converted.value,
              corridorParameterMargins: { innerMax: corridor.innerMax, innerMin: corridor.innerMin },
          })
        : null;
    const indicator = (goalStatus ? goalStatus : corridorStatus) ?? undefined;

    return (
        <Box
            display={'flex'}
            justifyContent={'flex-end'}
            alignItems={'flex-end'}
            paddingInlineEnd={u(2)}
            paddingBlock={u(1)}
            className={css.cell({
                isLast,
                indicator,
            })}
        >
            {converted.value ?? '\u2014'}
            {(converted.unit && isSpeedDisplayUnit(converted.unit)) || converted.symbol === 'cm' ? (
                <Box style={{ fontSize: '0.667em' }}>{converted.symbol}</Box>
            ) : (
                converted.symbol
            )}
        </Box>
    );
};
